import {announce} from '@github-ui/aria-live'
import {useCallback, useRef, useState} from 'react'

import {wrappedVerifiedFetch} from '../platform/functional-fetch-wrapper'
import {Resources} from '../strings'

interface UseRemoveAgentAssignmentOptions {
  assignmentId: number
  sourceRepoNwo: string
  onRemoveAssignment?: (assignmentId: number) => void
}

/**
 * Encapsulates the DELETE request and loading state for removing an agent
 * assignment from an issue. */
export function useRemoveAgentAssignment({
  assignmentId,
  sourceRepoNwo,
  onRemoveAssignment,
}: UseRemoveAgentAssignmentOptions) {
  const [isRemoving, setIsRemoving] = useState(false)
  const isRemovingRef = useRef(false)

  const removeAssignment = useCallback(async () => {
    if (isRemovingRef.current) return
    isRemovingRef.current = true
    setIsRemoving(true)
    try {
      await wrappedVerifiedFetch(`/${sourceRepoNwo}/issues/agent_assignments/${assignmentId}`, {
        method: 'DELETE',
      })
      onRemoveAssignment?.(assignmentId)
      announce(Resources.sessionRemovedFromIssue)
    } catch {
      announce(Resources.genericErrorMessage, {assertive: true})
    } finally {
      isRemovingRef.current = false
      setIsRemoving(false)
    }
  }, [assignmentId, sourceRepoNwo, onRemoveAssignment])

  return {removeAssignment, isRemoving}
}
